import { MediaFrame, MediaSprite } from '../../../types'
import { mediaFramesFromSource } from '../frames'
import { mediaInfoFromSource } from '../media-info'

const SPRITE_CACHE: { [key: string]: MediaSprite } = {}

export function mediaSpriteFromFrames(frames: MediaFrame[], maxSize?: number, fps?: number): MediaSprite | undefined {
    if (!frames.length) {
        return undefined
    }

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const duration = frames.reduce((a, b) => a + b.delay, 0)
    const totalFrames = fps ? Math.ceil(duration * fps) : frames.length
    const frameIndexes: number[] = []

    const frameCanvas = frames[0].canvas
    const frameAspectRatio = frameCanvas.width / frameCanvas.height
    let frameWidth = maxSize ? Math.min(frameCanvas.width, maxSize) : frameCanvas.width
    let frameHeight = Math.round(frameWidth / frameAspectRatio)

    if (maxSize && frameHeight > maxSize) {
        frameHeight = maxSize
        frameWidth = Math.round(frameHeight * frameAspectRatio)
    }

    const columns = Math.ceil(Math.sqrt(frames.length / frameAspectRatio))
    const rows = Math.ceil(frames.length / columns)

    frames.forEach((frame, index) => {
        const hold = frame.delay ? Math.max(1, Math.ceil((frame.delay / duration) * totalFrames)) : 1

        for (let i = 0; i < hold; i++) {
            frameIndexes.push(index)
        }
    })

    canvas.width = frameWidth * columns
    canvas.height = frameHeight * rows

    frames.forEach((frame, i) => {
        const x = (i % columns) * frameWidth
        const y = Math.floor(i / columns) * frameHeight

        context?.drawImage(frame.canvas, x, y, frameWidth, frameHeight)
    })

    return {
        canvas,
        columns,
        duration,
        frameHeight,
        frameIndexes,
        frameWidth,
        numFrames: frames.length,
        rows,
    }
}

export async function mediaSpriteFromSource(
    source: File | string,
    maxFrameSize: number = 480,
    targetFps?: number,
    cacheResults?: boolean
): Promise<MediaSprite | undefined> {
    let cacheId = typeof source === 'string' ? source : `${source.lastModified}${source.size}${source.name}`
    cacheId = maxFrameSize ? `${cacheId}-${maxFrameSize}` : cacheId
    cacheId = targetFps ? `${cacheId}-${targetFps}` : cacheId

    let fps = targetFps

    if (SPRITE_CACHE[cacheId]) {
        return SPRITE_CACHE[cacheId]
    }

    if (!fps) {
        const info = await mediaInfoFromSource(source)

        fps = info?.fps
    }

    const frames = await mediaFramesFromSource(source, undefined, true)
    const sprite = mediaSpriteFromFrames(frames, maxFrameSize, fps)

    if (cacheResults && sprite) {
        SPRITE_CACHE[cacheId] = sprite
    }

    return sprite
}
